import React, { useCallback, useContext, useEffect } from 'react';
import classnames from 'classnames';
import { LoadableComponentContext } from '@wix/da-hooks/pkg/useLoadableComponent';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import { useCommentPostAction } from '../../../CommentEditor/hooks/useCommentPostAction';
import { useCommentCancelAction } from '../../../CommentEditor/hooks/useCommentCancelAction';
import { CommentEditorContext } from '../../../CommentEditor/context';
import CommentEditor from '../../../CommentEditor';
import LoadingPlaceholder from '../LoadingPlaceholder';
import LazyPlaceholder from '../LazyPlaceholder';
import CommentBoxFooter from '../CommentBoxFooter';
import { useEditorContext } from '@wix/da-editor-tiptap/pkg/components/Editor/Context';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';

import { useDispatch } from 'react-redux';
import { resetEditorPublishResults } from '../../../CommentEditor/redux/actionCreators';

import s from './CommentBoxEditor.scss';

export interface Props {
  className?: string;

  onCancel?: () => void;
  onPost?: () => void;

  /** @deprecated Don't use this unless you know what you're doing, it's going away */
  nc?: {
    messageId: string;
    folderId: number | null;
    shouldDeleteMessage: boolean;
    postfix: React.ReactNode;
  };

  onEditorReady?: () => void;

  /** @deprecated Ideally make a new component instead */
  footerClassName?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

/** The core of our comment box. This is a `<CommentEditor />` with a footer and a loading indicator */
const CommentBoxEditor: React.FC<React.PropsWithChildren<Props>> = ({
  className,
  footerClassName,
  nc,
  onCancel,
  onPost,
  onEditorReady,
  ...restProps
}) => {
  const { isBusy, editorId, commentableItemId, commentableType, isExpanded, shouldResetEditor } =
    useContext(CommentEditorContext);
  const { isLoadingTriggered } = useContext(LoadableComponentContext);

  const postComment = useCommentPostAction({
    messageId: nc?.messageId,
    folderId: nc?.folderId,
    shouldDeleteMessage: nc?.shouldDeleteMessage,
    onPost,
  });
  const cancelComment = useCommentCancelAction({ onCancel });

  // Listen for mod+enter submit events coming from the editor
  const onKeyPost = useCallback(() => {
    postComment();
  }, [postComment]);
  const editor = useEditorContext();
  useEffect(() => {
    editor?.on('do-submit' as any, onKeyPost);
    return () => {
      editor?.off('do-submit' as any, onKeyPost);
    };
  }, [editor, onKeyPost]);

  const dispatch = useDispatch();
  useEffect(() => {
    if (shouldResetEditor) {
      dispatch(resetEditorPublishResults(editorId));
      cancelComment({ bypassConfirm: true });
    }
  }, [editorId, dispatch, shouldResetEditor, cancelComment]);

  return (
    <ErrorBoundary debugComponent="CommentBoxEditor">
      <div className={classnames(s['root'], isExpanded && s['expanded'], className)}>
        {isBusy && <LoadingIndicator className={s['loading']} />}

        {!isLoadingTriggered && <LazyPlaceholder />}

        {isLoadingTriggered && (
          <CommentEditor
            className={classnames(s['commentbox-editor'], isExpanded && s['expanded'])}
            loadingIndicator={<LoadingPlaceholder />}
            onEditorReady={onEditorReady}
            {...restProps}
          />
        )}

        {isLoadingTriggered && (
          <BiLoggerContextProvider
            value={{
              itemid:
                typeof commentableItemId === 'string'
                  ? parseInt(commentableItemId)
                  : commentableItemId,
              typeid: commentableType,
            }}
          >
            <CommentBoxFooter
              onCancel={cancelComment}
              onPost={postComment}
              className={classnames(s['commentbox-footer'], footerClassName)}
            />
          </BiLoggerContextProvider>
        )}
        {nc && nc.postfix}
      </div>
    </ErrorBoundary>
  );
};

CommentBoxEditor.displayName = 'CommentBoxEditor';

export default CommentBoxEditor;
